

import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  ORDER_STATUSES,
  PAYMENT_STATUSES,
  CONSIGNMENT_DESTINATIONS,
} from 'constants/statuses'
import { ROUTES } from 'routes'

import { toDollar, date, parseShopifyGid } from 'utils'

import {
  CopyIcon,
  CheckIcon,
  PackageIcon,
  TruckIcon,
  ArrowUpRightIcon,
  ChevronDownIcon,
  CircleIcon,
  PackageCheckIcon,
  CreditCardIcon,
  DownloadIcon,
  LinkIcon,
  WarehouseIcon,
} from 'lucide-react'
import { Payment } from '@mui/icons-material'

import { Box, Collapse, Divider, Stack, Grid, Typography, IconButton, Tooltip, useTheme } from '@mui/material'

// Read-only re-render of a customized tag: the Vera tag (round, tinted with the
// chosen color) with the logo positioned exactly as designed at checkout (from the
// stored placement transform). No snapshot image — rendered live from the data.
const TagDesignPreview = ({
  color,
  logoUrl,
  placement,
  size = 40,
}: {
  color?: string
  logoUrl?: string
  placement?: { xPct: number; yPct: number; scalePct: number; rotation: number } | null
  size?: number
}) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: color || '#e4e4e7',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
      border: '1px solid rgba(255,255,255,0.15)',
    }}
  >
    {logoUrl && (
      <Box
        component="img"
        src={logoUrl}
        alt="logo"
        sx={{
          position: 'absolute',
          left: `${placement?.xPct ?? 50}%`,
          top: `${placement?.yPct ?? 50}%`,
          width: `${placement?.scalePct ?? 45}%`,
          transform: `translate(-50%, -50%) rotate(${placement?.rotation ?? 0}deg)`,
          objectFit: 'contain',
        }}
      />
    )}
  </Box>
)

import { Card, Button, ShippingStepper, Skeleton, Modal } from '@repo/ui'
import {
  AssignPackageProductModal,
  SetConsignmentDestinationModal,
  EmptyState,
  StatusChip,
  Header,
  InfoItem,
  InfoRow,
  ProductCell,
  ProductGroupCell,
  UserCell,
  InfoTooltip,
} from 'components'
import { useSnackbar } from 'contexts'
import { useCanAccess, useReferrerState } from 'hooks'
import { useTagOrder, useTagOrderDocumentDownload } from 'hooks/api/useTagOrders'
import {
  useCurrentUserCompany,
  useInvoices,
  useTagOrderPackages,
  useTagProducts,
  type TagOrderPackage,
} from 'hooks/api'
import { useCart, CUSTOMIZATION_SETUP_FEE } from 'contexts/CartContext'
import type { TagShopOrderItem } from 'hooks/api/useTagOrders'

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function TagsOrderSkeleton() {
  return (
    <>
      <Header title="Order" breadcrumbsOveride="Order" />
      <Grid container spacing={4}>
        <Grid item md={8}>
          <Stack spacing={6}>
            <Card sx={{ p: 0 }}>
              <Stack p={6} spacing={4}>
                <Skeleton width="30%" />
                <Stack direction="row" spacing={4}>
                  <Skeleton width="40%" />
                  <Skeleton width="40%" />
                </Stack>
                <Stack direction="row" spacing={4}>
                  <Skeleton width="40%" />
                  <Skeleton width="40%" />
                </Stack>
              </Stack>
            </Card>
            <Card sx={{ p: 0 }}>
              <Stack p={6} spacing={4}>
                <Skeleton width="25%" />
                <Skeleton lines={3} />
              </Stack>
            </Card>
          </Stack>
        </Grid>
        <Grid item md={4}>
          <Card sx={{ p: 0 }}>
            <Stack p={6} spacing={4}>
              <Skeleton width="40%" />
              <Skeleton lines={4} />
              <Skeleton />
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const TagsOrderPage = () => {
  const { id } = useParams<{ id: string }>()
  const theme = useTheme()
  const navigate = useNavigate()
  const { addItem } = useCart()
  // Detail → detail: the derived label would be the Orders list, not this order.
  const referrer = useReferrerState('Order')

  const { data: order, isLoading } = useTagOrder(id)

  // The invoice for this order (its "View order" links back here via TAGS_ORDER,
  // so invoice.orderId === this page's :id). There's no by-order filter on the
  // list endpoint, so fetch a page of invoices and match client-side.
  const { data: invoicesData } = useInvoices({ limit: 100 })
  const orderInvoice = invoicesData?.invoices?.find(
    (inv) => inv.orderId && inv.orderId === id,
  )
  const [showTrackingDetails, setShowTrackingDetails] = useState(false)

  // ── VRG-282: the boxes of tags linked to this order ───────────────────────────
  //
  // Binding a box to one of the brand's products creates a certificate per tag in it, so the
  // contract manufacturer at the far end only has to place the tag. Deliberately NOT gated on
  // shipment — the value is certificates existing BEFORE the box lands.
  const {
    packages,
    isLoading: packagesLoading,
    isError: packagesError,
    refetch: refetchPackages,
  } = useTagOrderPackages(id)

  // The modal is shared with the internal Packages list, where a package can belong to any
  // company, so it reads `company_id` off the package object. Here the company is always the
  // caller's own — the endpoint is company-scoped — so it comes from the session instead of
  // being carried on every row of the payload.
  const companyQuery = useCurrentUserCompany()
  const companyId = companyQuery.data?.id ?? null

  const { isHasAccess: canBindPackageProduct } = useCanAccess({
    action: 'certificate_create_from_package',
  })

  const [assignPackage, setAssignPackage] = useState<TagOrderPackage | null>(null)

  // VRG-455 — where each box went. Same permission as binding a product: the same
  // person, deciding about the same box, on this same card.
  const [destinationPackage, setDestinationPackage] =
    useState<TagOrderPackage | null>(null)

  /**
   * The distinct tag-shop products this order was placed for.
   *
   * `TagShopProduct.id` is the NUMERIC Shopify product id (the API runs the GID through
   * `parseShopifyGid`), and so is a line item's `productId`, so the two compare directly.
   * The customization setup fee is a custom line item with no variant — excluded, it is not a
   * tag.
   */
  const orderTagProductIds = useMemo(() => {
    const ids = (order?.lineItems ?? [])
      .filter(line => line.variant?.id)
      .map(line => String(line.productId ?? ''))
      .filter(Boolean)
    return Array.from(new Set(ids))
  }, [order?.lineItems])

  /**
   * Fallback for the package's `tag_shop_product` — the tag SKU the box was purchased as, which
   * the product picker compares against to sort default-tag matches first.
   *
   * The packages endpoint does not return it yet (see `useTagOrderPackages`), and it cannot be
   * invented: pointing the picker at the WRONG tag would sort the wrong products first and
   * prompt to set the wrong default, which is worse than no ordering at all. So this only fires
   * when the order was placed for exactly ONE distinct tag product, in which case every box in
   * it must be that SKU. Multi-SKU orders need the server to say which box is which.
   */
  const soleOrderTagProductId =
    orderTagProductIds.length === 1 ? orderTagProductIds[0] : null
  const packagesNeedDerivedTag = packages.some(pkg => pkg.tag_shop_product === undefined)
  const tagProductsParams = useMemo(() => ({ limit: 100 }), [])
  const { data: tagProductsData } = useTagProducts(tagProductsParams, {
    enabled: packagesNeedDerivedTag && !!soleOrderTagProductId,
  })
  const derivedTagProduct = useMemo(
    () =>
      soleOrderTagProductId
        ? tagProductsData?.products?.find(
            entry => String(entry.id) === soleOrderTagProductId,
          ) ?? null
        : null,
    [tagProductsData?.products, soleOrderTagProductId],
  )

  /**
   * The duck-typed package the modal expects — the payload plus the two fields the brand-facing
   * endpoint does not carry. `tag_shop_product` is resolved explicitly rather than with `??` so
   * a server-sent `null` (this box genuinely has no tag SKU) is respected instead of being
   * overwritten by the derivation, and so the modal is never handed `undefined`.
   */
  const assignPackageObj = useMemo(
    () =>
      assignPackage
        ? {
            ...assignPackage,
            company_id: companyId,
            tag_shop_product:
              assignPackage.tag_shop_product !== undefined
                ? assignPackage.tag_shop_product
                : derivedTagProduct,
          }
        : null,
    [assignPackage, companyId, derivedTagProduct],
  )

  // VERA-891: the Vera-branded order PDF (company-scoped endpoint).
  const downloadOrderDocument = useTagOrderDocumentDownload()
  const handleDownloadOrderDocument = () => {
    if (!order) return
    downloadOrderDocument.mutate(
      { orderId: order.id, orderName: order.name },
      {
        onError: () =>
          notification({ type: 'error', text: 'Unable to download this order. Please try again.' }),
      },
    )
  }
  // Customization design modal (live re-render of the tag with its logo).
  const [designModal, setDesignModal] = useState<{
    customization: any
    side: 'front' | 'back'
  } | null>(null)

  const { open: notification } = useSnackbar()

  const handleCopy = useCallback((label: string, data: string) => {
    navigator.clipboard.writeText(data)
    notification({
      type: 'info',
      text: `The ${label} is copied to the clipboard`,
    })
  }, [notification])

  /**
   * "Order again" — reconstruct CartItems from the order's Shopify line items.
   *
   * CartItem.productId encodes "<numericProductId>#<numericVariantId>".
   * item.productId is now populated from the Shopify Admin API product.id GID
   * (VERA-847 fix). Falls back to variantId for both halves when absent.
   * If a line item lacks a variant id it is skipped; if NO items are added
   * we navigate to the shop instead of the empty cart.
   */
  const handleOrderAgain = useCallback(() => {
    if (!order) return
    let anyAdded = false
    for (const item of order.lineItems ?? []) {
      const rawVariantId = item.variant?.id
      if (!rawVariantId) continue
      const variantId = parseShopifyGid(rawVariantId)
      // Use the product id from the API when present (fixed in VERA-847).
      // Fall back to the variant id for both halves when the product id is
      // missing (older orders where the API did not return product.id).
      const resolvedProductId = item.productId || variantId
      const productId = `${resolvedProductId}#${variantId}`
      addItem({
        productId,
        variantId,
        sku: item.variant?.sku ?? '',
        title: item.title,
        variantTitle: item.variant?.title ?? item.title,
        unitPrice: Number(item.variant?.price ?? 0),
        casePrice: Number(item.variant?.price ?? 0),
        quantity: item.quantity ?? 1,
      })
      anyAdded = true
    }
    // If no line item had a valid variant id, navigate to the shop instead.
    navigate(anyAdded ? ROUTES.TAGS_SHOP_CART : ROUTES.TAGS_SHOP)
  }, [order, addItem, navigate])

  // ── Loading skeleton ────────────────────────────────────────────────────────
  if (isLoading) {
    return <TagsOrderSkeleton />
  }

  // ── Empty / not found ───────────────────────────────────────────────────────
  if (!order) {
    return (
      <>
        <Header title="Order" breadcrumbsOveride="Order" />
        <EmptyState
          title="No order data"
          text="Order details will appear once tag shop purchases are available."
          icon={<Payment sx={{ fontSize: 32 }} />}
          buttons={
            <Button variant="primary" onClick={() => navigate(ROUTES.TAGS_ORDERS)}>
              Back to Orders
            </Button>
          }
        />
      </>
    )
  }

  // ── Shipping stepper — prefer live Shopify shipmentStatus when present ───────
  // The fulfilment values are Shopify's `displayFulfillmentStatus` lowercased by
  // the API, which is exactly the ORDER_STATUSES key set the chip renders from.
  // Typing these as keys of that map means a rename there fails to compile here,
  // instead of silently leaving this branch dead (it read 'partial' before).
  const FULFILLED: keyof typeof ORDER_STATUSES = 'fulfilled'
  const PARTIALLY_FULFILLED: keyof typeof ORDER_STATUSES = 'partially_fulfilled'
  const shipmentStatusUpper = (order.shipmentStatus ?? '').toUpperCase()
  const stepIndex = shipmentStatusUpper
    ? shipmentStatusUpper === 'DELIVERED'
      ? 2
      : ['IN_TRANSIT', 'OUT_FOR_DELIVERY', 'ATTEMPTED_DELIVERY'].includes(shipmentStatusUpper)
      ? 1
      : 0
    : order.fulfillmentStatus === FULFILLED
    ? 2
    : order.fulfillmentStatus === PARTIALLY_FULFILLED
    ? 1
    : 0

  const shippingAddr = order.shippingAddress
  const formattedAddress = [
    shippingAddr?.address1,
    shippingAddr?.city,
    shippingAddr?.country,
    shippingAddr?.zip,
  ]
    .filter(Boolean)
    .join(', ')

  const track = {
    carrier: order.trackingCompany,
    number: order.trackingNumber,
    url: order.trackingUrl,
    status: order.shipmentStatus,
    shippedAt: order.createdAt,
    eta: order.estimatedDeliveryAt,
    // TODO(VRG-189): always [] — Shopify native has no checkpoint history, so
    // there is no real event timeline to render yet. A tracking aggregator
    // (17track/AfterShip/EasyPost) adapter would fill this with real checkpoints;
    // see .vergence/work/tasks/VRG-189-Tags.Order.Tracking.Checkpoints.md
    events: [] as {
      label: string
      at: string
      state: 'done' | 'current' | 'future'
    }[],
  }
  const trackStepIndex = stepIndex

  // Customization: checkout adds a "Customization setup fee" custom line item
  // (no variant) per customized item — used to break the fee out in the summary.
  // The design (logos, color/finish) is persisted on each product line item as
  // Shopify properties and surfaced as `line.customization`.
  // VRG-575: was a hardcoded 100 — the fourth copy of the fee. Now the one
  // constant from @repo/types, which the API's billing path reads too.
  //
  // ⚠️ This page renders a PAST order, so it should ideally show what THAT order
  // was charged rather than today's price. The Shopify custom line item carries
  // the amount actually billed; using it here instead of the constant is the
  // correct fix and is left alone rather than half-done — this change keeps the
  // pre-existing behaviour and removes the duplicate literal, no more.
  const customizationLine = order.lineItems?.find(
    l => !l.variant?.id && /customization/i.test(l.title),
  )
  const customizationCount = customizationLine?.quantity ?? 0
  // Real product lines only (exclude the customization fee custom line item).
  const productLines = (order.lineItems ?? []).filter(l => l.variant?.id)
  // Product lines that carry a design (with retrievable logos).
  const customizedProducts = productLines.filter(
    l => l.customization?.frontLogoUrl || l.customization?.backLogoUrl,
  )
  const isCustomized = customizationCount > 0 || customizedProducts.length > 0

  // Small labelled logo thumbnail for the Customization field.
  // Clickable design thumbnail — the tag re-rendered with its logo. Opens the
  // full front/back design modal.
  const designThumb = (
    customization: any,
    side: 'front' | 'back',
    label: string,
  ) => {
    const url = side === 'front' ? customization.frontLogoUrl : customization.backLogoUrl
    const placement =
      side === 'front' ? customization.frontPlacement : customization.backPlacement
    return (
      <Stack
        key={`${label}-${url}`}
        spacing={1}
        alignItems="center"
        onClick={() => setDesignModal({ customization, side })}
        sx={{ cursor: 'pointer' }}
      >
        <TagDesignPreview
          color={customization.color}
          logoUrl={url}
          placement={placement}
          size={48}
        />
        <Typography variant="body4" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    )
  }

  return (
    <>
      {/* Standard page header, matching the internal Order detail page: title +
          status on the left, actions right-aligned. (Previously a
          breadcrumbs-only bar with the title and actions inlined in the left
          column, so the summary would line up with the cart's title row — the
          summary now sits below, so that constraint is gone.) */}
      <Header
        title={order.name || `#ORD-${parseShopifyGid(order.id)}`}
        breadcrumbsOveride={order.name || parseShopifyGid(order.id)}
        status={{ statuses: ORDER_STATUSES, value: order.fulfillmentStatus }}
        rightComponents={
          <Stack direction="row" spacing={4} alignItems="center">
            {/* Vera-branded order PDF — always available (Vera owns the order
                data), unlike the invoice which depends on Stripe. */}
            <Button
              variant="secondary"
              loading={downloadOrderDocument.isPending}
              startIcon={<DownloadIcon strokeWidth={1} />}
              onClick={handleDownloadOrderDocument}
            >
              Download
            </Button>
            {orderInvoice ? (
              <Button
                variant="secondary"
                startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                onClick={() => navigate(ROUTES.SETTINGS_COMPANY_INVOICE(orderInvoice.id), { state: referrer })}
              >
                View invoice
              </Button>
            ) : (
              <Tooltip title="No invoice for this order yet">
                <span>
                  <Button
                    variant="secondary"
                    disabled
                    startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                  >
                    View invoice
                  </Button>
                </span>
              </Tooltip>
            )}
          </Stack>
        }
      />

      <Grid container spacing={4}>
        {/* ── Left column: details + tracking ─────────────────────────────── */}
        <Grid item md={8}>
          <Stack spacing={6}>
            {/* Details card. `pb: 2` because InfoItem carries its own 16px
                bottom padding — 16 + 8 lands the last row 24px off the card's
                edge, matching the 24px title inset and the internal page's
                uniform p:6. Without it the last row sat ~1px off the edge. */}
            <Card sx={{ p: 0, pb: 2 }}>
              <Stack p={6} pb={4} direction="row" alignItems="center">
                <Typography variant="body1">Details</Typography>
              </Stack>
              <InfoRow>
                <InfoItem label="Date" value={date(order.createdAt)} />
                <InfoItem label="Confirmation #" value={order.name || '—'} />
              </InfoRow>
              <InfoRow>
                <InfoItem
                  label="Total"
                  value={toDollar(Number(order.totalPriceSet?.shopMoney?.amount ?? 0))}
                />
                <InfoItem
                  label="Payment status"
                  value={
                    order.financialStatus && order.financialStatus in PAYMENT_STATUSES ? (
                      <StatusChip
                        size="small"
                        status={order.financialStatus}
                        statuses={PAYMENT_STATUSES}
                      />
                    ) : (
                      order.financialStatus ?? '—'
                    )
                  }
                />
              </InfoRow>
              <InfoRow>
                <InfoItem
                  label="Products"
                  value={
                    productLines.length ? (
                      <ProductGroupCell
                        products={productLines.map(l => ({
                          title: l.title,
                          sku: l.variant?.title
                            ? `Quantity: ${l.variant.title}`
                            : l.variant?.sku || '',
                          quantity: l.quantity,
                          id: l.variant?.id ?? l.id,
                          images: l.image ? [l.image] : [],
                        }))}
                        route={ROUTES.TAGS_SHOP_PRODUCT}
                        showQuantityBadge
                      />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoItem
                  label="Customization"
                  value={
                    customizedProducts.length ? (
                      <Stack direction="row" spacing={3} sx={{ pt: 1 }}>
                        {customizedProducts.flatMap(l =>
                          [
                            l.customization?.frontLogoUrl
                              ? designThumb(l.customization, 'front', 'Front')
                              : null,
                            l.customization?.backLogoUrl
                              ? designThumb(l.customization, 'back', 'Back')
                              : null,
                          ].filter(Boolean),
                        )}
                      </Stack>
                    ) : isCustomized ? (
                      'Customized'
                    ) : (
                      'None'
                    )
                  }
                />
              </InfoRow>
              <InfoRow>
                <InfoItem
                  label="Ordered by"
                  value={
                    // The person who placed the order (captured from the checkout
                    // session, stored on the order note_attributes). The Shopify
                    // customer is the *company*, so it is never used as a fallback
                    // here — an unknown orderer shows "—", not the brand name.
                    order.orderedBy?.name || order.orderedBy?.email ? (
                      <UserCell
                        user={{
                          name: order.orderedBy?.name,
                          email: order.orderedBy?.email,
                        }}
                      />
                    ) : (
                      '—'
                    )
                  }
                />
                <InfoItem
                  label="Payment method"
                  value={
                    order.paymentMethod ? (
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            flexShrink: 0,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: `1px solid ${theme.palette.primary[100]}`,
                            color: 'text.secondary',
                          }}
                        >
                          <CreditCardIcon size={16} strokeWidth={1} />
                        </Box>
                        <Typography variant="body2">
                          {order.paymentMethod.brand.charAt(0).toUpperCase() +
                            order.paymentMethod.brand.slice(1)}{' '}
                          •••• {order.paymentMethod.last4}
                        </Typography>
                      </Stack>
                    ) : (
                      '—'
                    )
                  }
                />
              </InfoRow>
              <InfoRow>
                <InfoItem
                  label="Delivery address"
                  value={
                    formattedAddress
                      ? order.shippingAddress?.label
                        ? `${order.shippingAddress.label} · ${formattedAddress}`
                        : formattedAddress
                      : '—'
                  }
                />
              </InfoRow>
            </Card>

            {/* VRG-282 — Packages card. Sits above Tracking because binding is the one
                ACTION this page offers, and it is only worth doing before the box is opened
                at the other end. Rendered even when empty: "why is there nothing to bind
                yet" is a real question for a brand who has been told this exists. */}
            <Card sx={{ p: 0 }}>
              <Stack p={6} spacing={4}>
                <Stack spacing={2}>
                  {/* The card's own paragraph covers what assigning DOES. The entry's
                      expanded body covers what it does not: a package can be reassigned
                      while none of its certificates has been activated, and is frozen
                      once one has — which is the rule that governs the Assign action
                      immediately below this. */}
                  <Typography variant="body1">
                    Packages
                    <InfoTooltip term="package" />
                  </Typography>
                  <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Assign a product to a box and every tag in it becomes a certificate, so
                    whoever receives it only has to place the tag. Best for when your
                    manufacturing is done in another location.
                  </Typography>
                </Stack>

                {packagesLoading ? (
                  <Skeleton lines={2} />
                ) : packagesError ? (
                  // Inline, not a toast — an error is the answer to something on this card,
                  // and reading a failed fetch as "no packages yet" would be a lie.
                  <Typography variant="body2" color="error">
                    Unable to load the packages for this order. Please refresh to try again.
                  </Typography>
                ) : packages.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Packages appear here once Vera fills the boxes for this order and links
                    them to it.
                  </Typography>
                ) : (
                  <Stack spacing={4}>
                    {packages.map((pkg, index) => (
                      <Stack key={pkg.id} spacing={4}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={4}
                        >
                          <Stack spacing={1} sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" noWrap>
                              {pkg.name}
                            </Typography>
                            <Typography variant="body3" color="text.secondary">
                              {pkg.quantity} {pkg.quantity === 1 ? 'tag' : 'tags'}
                              {' · '}
                              {/* VRG-455 — stated on the row, not hidden behind
                                  the modal that sets it. A box reading "Not
                                  recorded" has certificates a buyer's scan can
                                  still claim, so the state has to be visible
                                  without opening anything. */}
                              {
                                CONSIGNMENT_DESTINATIONS[
                                  pkg.consignment_destination ?? 'unknown'
                                ].label
                              }
                            </Typography>
                          </Stack>

                          {/* The shared ProductCell — image plate, link, and the referrer
                              state that gives the product page a "← Back" to here (§42).
                              The payload projection carries a single `image`; the cell reads
                              the catalog's `images[]`, adapted here rather than special-casing
                              the shared cell (same adaptation as the Packages list). */}
                          {pkg.product?.id ? (
                            <ProductCell
                              product={{
                                ...pkg.product,
                                images: pkg.product.image ? [pkg.product.image] : [],
                              }}
                              route={ROUTES.PRODUCT(pkg.product.id)}
                              size={40}
                              width={160}
                            />
                          ) : (
                            <Typography variant="body3" color="text.secondary">
                              No product assigned
                            </Typography>
                          )}

                          {/* Two secondary actions acting as one group — 12px
                              (§29, on the platform's 4px base). */}
                          <Stack direction="row" spacing={3} sx={{ flexShrink: 0 }}>
                            {/* VRG-455 — the eligible-set control. Same
                                permission as the product binding beside it, and
                                the shared Button's own disabledReason rather
                                than a Tooltip wrapper, so the disabled control
                                explains itself instead of swallowing the click
                                (§46). */}
                            <Button
                              variant="secondary"
                              startIcon={<WarehouseIcon strokeWidth={1} />}
                              disabled={!canBindPackageProduct}
                              disabledReason={
                                canBindPackageProduct
                                  ? undefined
                                  : "You need the 'Create from package' permission. Ask an admin to grant it."
                              }
                              onClick={() => setDestinationPackage(pkg)}
                            >
                              Set destination
                            </Button>

                            <Tooltip
                              title={
                                !canBindPackageProduct
                                  ? "You need the 'Create from package' permission. Ask an admin to grant it."
                                  : ''
                              }
                            >
                              {/* A disabled MUI Button fires no events, so the tooltip needs a
                                  wrapper to hang off — same pattern as "View invoice" above. */}
                              <span>
                                <Button
                                  variant="secondary"
                                  startIcon={<LinkIcon strokeWidth={1} />}
                                  // Also held until the company resolves: the picker lists that
                                  // company's products, so opening early would show an empty one.
                                  disabled={!canBindPackageProduct || !companyId}
                                  onClick={() => setAssignPackage(pkg)}
                                >
                                  {pkg.product ? 'Change product' : 'Assign product'}
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </Stack>
                        {index !== packages.length - 1 && <Divider />}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>

            {/* Tracking card */}
            <Card sx={{ p: 0 }}>
              <Stack p={6} spacing={4}>
                {/* No status pill here — the page header already carries the
                    order's fulfillment status, and repeating it beside the card
                    title read as duplicated state. */}
                <Typography variant="body1">Tracking</Typography>

                {track.number ? (
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={3}>
                      <InfoItem
                        // Shrink to fit — InfoItem's built-in width:100% + px:6 would
                        // otherwise push the button group past the card's right gutter.
                        sx={{ px: 0, width: 'auto', minWidth: 0, flex: 1 }}
                        label={track.carrier || 'Tracking number'}
                        value={
                          track.url && track.url !== '#' ? (
                            <a href={track.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                              {track.number}
                            </a>
                          ) : (
                            track.number
                          )
                        }
                      />
                      <Stack direction="row" spacing={2} alignItems="center" sx={{ flexShrink: 0 }}>
                        <IconButton onClick={() => handleCopy('tracking number', track.number || '')}>
                          <CopyIcon strokeWidth={1} />
                        </IconButton>
                        {track.url && (
                          <Button
                            variant="secondary"
                            startIcon={<ArrowUpRightIcon strokeWidth={1} />}
                            onClick={() =>
                              window.open(track.url, '_blank', 'noopener,noreferrer')
                            }
                          >
                            View tracking
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    <Divider />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No tracking number yet.
                  </Typography>
                )}

                <Stack spacing={1} pt={4}>
                  <ShippingStepper
                    steps={[
                      { label: 'Shipped', icon: <PackageIcon strokeWidth={1} /> },
                      { label: 'On the way', icon: <TruckIcon strokeWidth={1} /> },
                      {
                        label: track.eta
                          ? `Est. Arrival ${date(track.eta)}`
                          : 'Estimated Arrival',
                        icon: <CheckIcon strokeWidth={1} />,
                      },
                    ]}
                    activeStep={trackStepIndex}
                  />
                </Stack>

                {/* Detailed carrier event timeline (shown when events exist). */}
                {track.events.length > 0 && (
                  <Stack spacing={3} pt={2}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      onClick={() => setShowTrackingDetails(v => !v)}
                      sx={{ cursor: 'pointer', width: 'fit-content' }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ textDecoration: 'underline', fontWeight: 500 }}
                      >
                        {showTrackingDetails ? 'Show less details' : 'Show more details'}
                      </Typography>
                      <ChevronDownIcon
                        strokeWidth={1}
                        size={18}
                        style={{
                          transform: showTrackingDetails ? 'rotate(180deg)' : 'none',
                          transition: 'transform 150ms',
                        }}
                      />
                    </Stack>
                    <Collapse in={showTrackingDetails}>
                      <Stack spacing={0}>
                        {track.events.map((ev, i) => {
                          const isCurrent = ev.state === 'current'
                          const isDone = ev.state === 'done'
                          const PURPLE =
                            'linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55, 39, 137) 100%)'
                          // ONLY the current stage is purple — done + future both
                          // render as muted zinc circles (grey check for done, grey
                          // outline for future). All-purple read as redundant with the
                          // horizontal stepper above; the current-only accent is the
                          // one thing this detailed timeline adds.
                          const Icon = isCurrent
                            ? TruckIcon
                            : isDone
                              ? CheckIcon
                              : i === 0
                                ? PackageCheckIcon
                                : CircleIcon
                          return (
                            <Stack
                              key={ev.label}
                              direction="row"
                              spacing={3}
                              alignItems="flex-start"
                              sx={{ position: 'relative', pb: 4 }}
                            >
                              {/* connector line — centred on the 32px circles (x=16) */}
                              {i !== track.events.length - 1 && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    left: 16,
                                    top: 16,
                                    bottom: -16,
                                    width: '2px',
                                    transform: 'translateX(-50%)',
                                    bgcolor:
                                      theme.palette.mode === 'dark' ? '#3f3f46' : '#eaeaf0',
                                  }}
                                />
                              )}
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: '50%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                  zIndex: 1,
                                  backgroundImage:
                                    isCurrent ? PURPLE : 'none',
                                  bgcolor:
                                    isCurrent
                                      ? undefined
                                      : theme.palette.mode === 'dark'
                                        ? '#27272a'
                                        : theme.palette.background.paper,
                                  border:
                                    isCurrent
                                      ? 'none'
                                      : `1px solid ${
                                          theme.palette.mode === 'dark'
                                            ? '#3f3f46'
                                            : theme.palette.divider
                                        }`,
                                  color:
                                    isCurrent
                                      ? '#fff'
                                      : theme.palette.mode === 'dark'
                                        ? '#a1a1aa'
                                        : theme.palette.text.secondary,
                                }}
                              >
                                <Icon strokeWidth={1} size={16} />
                              </Box>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ flex: 1, pt: 1 }}
                              >
                                <Typography
                                  variant="body2"
                                  color={ev.state === 'future' ? 'text.secondary' : 'text.primary'}
                                >
                                  {ev.label}
                                </Typography>
                                {ev.at && (
                                  <Typography variant="body3" color="text.secondary">
                                    {date(ev.at)}
                                  </Typography>
                                )}
                              </Stack>
                            </Stack>
                          )
                        })}
                      </Stack>
                    </Collapse>
                  </Stack>
                )}
              </Stack>
            </Card>
          </Stack>
        </Grid>

        {/* ── Right column: order summary ──────────────────────────────────── */}
        <Grid item md={4}>
          <Card sx={{ p: 0 }}>
            <Stack p={6} spacing={4}>
              <Typography pb={6} variant="body1">Summary</Typography>
              <Stack spacing={4} pb={6}>
                {productLines.map((item, index) => (
                  <Stack spacing={2} key={`order-item-${index}`}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2">{item.title}</Typography>
                      <Typography variant="body2">
                        {toDollar(Number(item.variant?.price ?? 0))}
                      </Typography>
                    </Stack>
                    <Typography variant="body3" color="text.secondary">
                      {item.quantity} {item.quantity === 1 ? 'piece' : 'pieces'}
                    </Typography>
                    <Divider />
                  </Stack>
                ))}
                {/* Customization fee broken out as its own line — mirrors the
                    checkout summary. Flat $100 per customized item. */}
                {isCustomized && (
                  <>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">
                        Customization
                        {customizationCount > 1 ? ` (${customizationCount})` : ''}
                      </Typography>
                      <Typography variant="body2">
                        {toDollar(customizationCount * CUSTOMIZATION_SETUP_FEE)}
                      </Typography>
                    </Stack>
                    {/* Divider only when customization is present — the last product
                        line already renders its own divider, so a standalone one here
                        doubled up (an extra rule) on non-customized orders. */}
                    <Divider />
                  </>
                )}

                {/* Subtotal (products + customization) then the order Total, which
                    also carries any tax/shipping Shopify computed. */}
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    {toDollar(
                      productLines.reduce(
                        (s, l) =>
                          s +
                          Number(l.variant?.price ?? 0) * Number(l.quantity ?? 0),
                        0,
                      ) + customizationCount * CUSTOMIZATION_SETUP_FEE,
                    )}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body1">Total</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {toDollar(Number(order.totalPriceSet?.shopMoney?.amount ?? 0))}
                  </Typography>
                </Stack>
              </Stack>
              <Typography
                variant="body3"
                color="text.secondary"
                sx={{lineHeight: 1.5}}
              >
                Tags are non-refundable. Once shipped, they are permanently
                locked to your company.
              </Typography>

              <Button disableElevation fullWidth onClick={handleOrderAgain}>
                Order again
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* VRG-282 — the binding modal, shared with the internal Packages list. It owns the
          picker, the inherited activation trigger, the 409 refusal and the set-as-default
          follow-up, so nothing about that flow is re-stated here.
          No success toast: the result lands on the row behind the modal (§ toast policy). */}
      <AssignPackageProductModal
        packageObj={assignPackageObj}
        open={!!assignPackage}
        onClose={() => setAssignPackage(null)}
        refetch={refetchPackages}
      />

      {/* VRG-455 — where the box went, which decides whether a buyer's scan can
          claim the certificates for the tags in it. Shared with the internal
          Packages list; the modal owns the options, the consequence copy and
          the confirm. */}
      <SetConsignmentDestinationModal
        packageObj={destinationPackage}
        open={!!destinationPackage}
        onClose={() => setDestinationPackage(null)}
        refetch={refetchPackages}
        canSet={canBindPackageProduct}
      />

      {/* Customization design modal — live re-render of the tag + logo, with a
          front/back toggle when the design has both sides. */}
      <Modal
        title="Tag design"
        open={!!designModal}
        onClose={() => setDesignModal(null)}
      >
        {designModal && (
          <Stack spacing={5} alignItems="center" sx={{ py: 2 }}>
            {designModal.customization.backLogoUrl && (
              <Stack direction="row" spacing={2}>
                {(['front', 'back'] as const).map(side => (
                  <Button
                    key={side}
                    variant={designModal.side === side ? 'primary' : 'secondary'}
                    onClick={() =>
                      setDesignModal(d => (d ? { ...d, side } : d))
                    }
                  >
                    {side === 'front' ? 'Front' : 'Back'}
                  </Button>
                ))}
              </Stack>
            )}
            <TagDesignPreview
              color={designModal.customization.color}
              logoUrl={
                designModal.side === 'front'
                  ? designModal.customization.frontLogoUrl
                  : designModal.customization.backLogoUrl
              }
              placement={
                designModal.side === 'front'
                  ? designModal.customization.frontPlacement
                  : designModal.customization.backPlacement
              }
              size={280}
            />
            <Stack direction="row" spacing={6} justifyContent="center">
              {designModal.customization.color && (
                <InfoItem
                  spacing={false}
                  label="Color"
                  value={designModal.customization.color}
                />
              )}
              {designModal.customization.finish && (
                <InfoItem
                  spacing={false}
                  label="Tag finish"
                  value={designModal.customization.finish}
                />
              )}
              {designModal.customization.logoFinish && (
                <InfoItem
                  spacing={false}
                  label="Logo finish"
                  value={designModal.customization.logoFinish}
                />
              )}
            </Stack>
          </Stack>
        )}
      </Modal>
    </>
  )
}
